import React, { useEffect, useState } from 'react';
import { asyncScheduler } from 'rxjs';
import FlipMove from 'react-flip-move';
import useStyles from 'isomorphic-style-loader/useStyles';

import { Summary, Button } from '@sololearnorg/sol';
import { ILeaderboardUser, ILevelZone } from '../../../symphony';

import { useFadeOut } from '../SlHooks/useFadeOut';

import { LeaderboardUser } from '../LeaderboardUser/LeaderboardUser';
import { LevelIndicator, LevelZone } from '../LevelZone/LevelZone';
import { SlTransition } from '../SlTransition/SlTransition';

import s from './SlLeaderboardCelebrationScreen.scss';

export interface ILeaderboardUserScreen extends ILeaderboardUser {
	position: number;
}

export interface ISlLeaderboardCelebrationScreenProps {
	previouseLeaderboardUsers?: (ILeaderboardUserScreen | ILevelZone)[];
	currentLeaderboardUsers: (ILeaderboardUserScreen | ILevelZone)[];
	userId: string;
	place: number;
	leagueName: string;
	onContinue: () => void;
	goToLeaderboard: () => void;
}

export const SlLeaderboardCelebrationScreen: React.FC<ISlLeaderboardCelebrationScreenProps> = React.memo(
	({
		onContinue,
		goToLeaderboard,
		place,
		leagueName,
		currentLeaderboardUsers,
		previouseLeaderboardUsers,
		userId,
	}) => {
		useStyles(s);
		const [users, setUsers] = useState<(ILeaderboardUserScreen | ILevelZone)[]
		>(previouseLeaderboardUsers || currentLeaderboardUsers);

		const { isFadeOutStarted, startFadeOut } = useFadeOut(600);

		useEffect(() => {
			asyncScheduler.schedule(() => {
				setUsers(currentLeaderboardUsers);
			}, 0);
		}, []);

		const renderUsers = (): JSX.Element[] => users.map((item) => {
			if ((item as ILevelZone)?.isLevelUpZone) {
				return <LevelZone zone={LevelIndicator.UP} key="up" />;
			}
			if ((item as ILevelZone)?.isLevelDownZone) {
				return (
					<LevelZone zone={LevelIndicator.DOWN} key="down" />
				);
			}
			return (
				<LeaderboardUser
					user={item as ILeaderboardUserScreen}
					position={(item as ILeaderboardUserScreen).position}
					key={(item as ILeaderboardUserScreen).userId}
					isHighlighted={
						(item as ILeaderboardUserScreen).userId
						=== Number(userId)
					}
				/>
			);
		});

		return (
			<div className="ldb-celebration-screen">
				<Summary
					headline={(
						<SlTransition
							fadeIn={{ delay: 0.1, duration: 0.3 }}
							fadeOut={{ delay: 0, duration: 0.3 }}
							isFadeOutStarted={isFadeOutStarted}
						>
							<div className="ldb-celebration-screen__headline">
								You are now in place #
								{place}
								{' '}
								<br />
								{' '}
								of the
								{' '}
								{leagueName}
								&nbsp;League 🌟
							</div>
						</SlTransition>
					)}
					description={(
						<SlTransition
							fadeIn={{ delay: 0.3, duration: 0.3 }}
							fadeOut={{ delay: 0.1, duration: 0.3 }}
							isFadeOutStarted={isFadeOutStarted}
						>
							<div className="ldb-celebration-screen__users">
								<SlTransition
									fadeIn={{ delay: 0.6, duration: 0.3 }}
									fadeOut={{ delay: 0.2, duration: 0.3 }}
									isFadeOutStarted={isFadeOutStarted}
								>
									<FlipMove
										enterAnimation="accordionVertical"
										duration={1000}
										delay={900}
									>
										{renderUsers()}
									</FlipMove>
								</SlTransition>
							</div>
						</SlTransition>
					)}
					actionBar
					actions={[
						<SlTransition
							fadeIn={{ delay: 1.6, duration: 0.3 }}
							fadeOut={{ delay: 0.3, duration: 0.3 }}
							isFadeOutStarted={isFadeOutStarted}
						>
							<Button
								key="goToLeaderboard"
								onClick={() => startFadeOut(goToLeaderboard)}
								dataTestid="goToLeaderboardBtn"
								buttonType="secondary"
								className="ldb-celebration-screen__sec-button"
							>
								Go to the Leaderboard
							</Button>
						</SlTransition>,
						<SlTransition
							fadeIn={{ delay: 1.6, duration: 0.3 }}
							fadeOut={{ delay: 0.3, duration: 0.3 }}
							isFadeOutStarted={isFadeOutStarted}
						>
							<Button
								key="Continue"
								onClick={() => startFadeOut(onContinue)}
								dataTestid="continueBtn"
							>
								Continue
							</Button>
						</SlTransition>,
					]}
				/>
			</div>
		);
	},
);
